import type {
  FeaturedThreadResPagedData,
  HeadlineResponse,
  NilHundredResponseNil100FilterResponseRelatedPagedData,
  OrgCompositeRankingResRelatedRankingResponseRelatedPagedData,
  PlayerItemResponsePagedData,
  SiteResponse,
  TeamRankingResponseRelatedTeamRankingResponseRelatedPagedData,
  TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { ServiceLogo } from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { ExcoPlayer } from '@public/components/Ads/ExcoPlayer/ExcoPlayer';
import { MobileMPU } from '@public/components/Ads/MobileMPU';
import { MPU } from '@public/components/Ads/MPU';
import { TopRankings as CollegeRankings } from '@public/components/College/Modules/TopRankings/TopRankings';
import { CoverList } from '@public/components/Headlines/ArticleCover/CoverList';
import { FeedList } from '@public/components/Headlines/ArticleFeed/FeedList';
import { TopRankings as HighSchoolRankings } from '@public/components/HighSchool/Modules/TopRankings/TopRankings';
import { SEOHomeText } from '@public/components/Home/SEOHomeText';
import { HomeLayout } from '@public/components/Layouts/HomeLayout';
import { MegaboardModule } from '@public/components/MegaBoard/Module';
import { SignUpFooter } from '@public/components/Newsletter/SignUpFooter';
import { SignUpModule } from '@public/components/Newsletter/SignUpModule';
import { Nil100Widget } from '@public/components/Nil/Nil100Widget';
import { Connect } from '@public/components/Social/Connect';
import { FanSiteList } from '@public/components/Teams/FanSiteList';
import { LatestCommits } from '@public/components/Teams/LatestCommits';
import { TopHeadlines } from '@public/components/TopHeadlines';
import { on3Data } from '@public/store/mocks/Connect.mock';
import { getArticleLists } from '@public/utils/articles';

import styles from './NetworkHome.module.scss';

export interface INetworkPageProps {
  featuredArticles: HeadlineResponse[] | null;
  collegeSportsArticles: HeadlineResponse[] | null;
  recruitingArticles: HeadlineResponse[] | null;
  collegeBusinessArticles: HeadlineResponse[] | null;
  nilHundred: NilHundredResponseNil100FilterResponseRelatedPagedData | null;
  latestCommits: PlayerItemResponsePagedData | null;
  hsArticles: HeadlineResponse[] | null;
  highSchoolRankings: OrgCompositeRankingResRelatedRankingResponseRelatedPagedData | null;
  collegeRankings: TeamRankingResponseRelatedTeamRankingResponseRelatedPagedData | null;
  trendingList: TrendingListItemResponseTrendingListResponseRelatedPagedData | null;
  fanSites: SiteResponse[] | null;
  megaboardData: FeaturedThreadResPagedData | null;
}

const sportViewAll = [
  {
    url: 'category/football/news/',
    text: 'View All Football',
    title: 'Football News',
  },
  {
    url: 'category/basketball/news/',
    text: 'View All Basketball',
    title: 'Basketball News',
  },
];

const recruitingViewAll = [
  {
    url: 'rivals/category/football-recruiting/news/',
    text: 'View All Football Recruiting',
    title: 'Football Recruiting News',
  },
  {
    url: 'rivals/category/basketball-recruiting/news/',
    text: 'View All Basketball Recruiting',
    title: 'Basketball Recruiting News',
  },
];

const businessViewAll = [
  {
    url: 'nil/news/',
    text: 'View All NIL',
    title: 'NIL News',
  },
];
const hsViewAll = [
  {
    url: 'high-school/news/',
    text: 'View All High School News',
    title: 'High School News',
  },
];

export const NetworkHome = ({
  featuredArticles,
  collegeSportsArticles,
  recruitingArticles,
  collegeBusinessArticles,
  nilHundred,
  latestCommits,
  highSchoolRankings,
  collegeRankings,
  hsArticles,
  trendingList,
  fanSites,
  megaboardData,
}: INetworkPageProps) => {
  const teamSites = fanSites?.filter((site) => site.live && site.inIndex);
  const { list } = nilHundred || {};

  const [
    coverArticles,
    collegeSports,
    recruiting,
    collegeBusiness,
    hsArticleList,
  ] = getArticleLists({
    featuredArticles: {
      list: featuredArticles ?? [],
      sliceLimit: 3,
    },
    lists: [
      {
        list: collegeSportsArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: recruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: collegeBusinessArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: hsArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
    ],
  });

  const hasHighSchoolRankings = !!highSchoolRankings?.list?.length;
  const hasCollegeRankings = !!collegeRankings?.list?.length;

  return (
    <div className={styles.layout}>
      <SEOHomeText className={styles.headline}>
        <ServiceLogo type="on3" />
        <h1>
          Delivering trusted college and high school sports news, analysis,
          data, and insights to fans, athletes, schools, and brands
        </h1>
      </SEOHomeText>
      <HomeLayout>
        <div className={styles.center}>
          <div className={styles.inner}>
            {!!coverArticles?.length && (
              <div className={styles.coverArticles}>
                <CoverList
                  authorColor="#6F747B"
                  backgroundColor="transparent"
                  isHome
                  list={coverArticles}
                  titleColor="#1D252C"
                />
              </div>
            )}

            {!!trendingList && (
              <div className={styles.trending}>
                <TopHeadlines trendingList={trendingList} />
                <SignUpModule title="Get the Daily On3 Newsletter to your inbox every morning" />
              </div>
            )}

            <ExcoPlayer
              className={styles.excoHub}
              playerId="b1a33dcb-6ecd-44f9-a1a3-096bb1aef51e"
            />

            <div className={styles.feeds}>
              {!!megaboardData?.list?.length && (
                <MegaboardModule threads={megaboardData.list} />
              )}

              {!!collegeSports?.length && (
                <FeedList
                  list={collegeSports}
                  section="College Sports"
                  viewAllLinks={sportViewAll}
                />
              )}

              <MobileMPU id="mobile_mpu_top" incontent />

              {!!recruiting?.length && (
                <FeedList
                  brand="rivals"
                  list={recruiting}
                  section="Recruiting"
                  viewAllLinks={recruitingViewAll}
                />
              )}

              {!!collegeBusiness?.length && (
                <FeedList
                  list={collegeBusiness}
                  section="NIL"
                  viewAllLinks={businessViewAll}
                />
              )}

              {!!hsArticles?.length && (
                <FeedList
                  brand="rivals"
                  list={hsArticleList}
                  section="High School"
                  viewAllLinks={hsViewAll}
                />
              )}
            </div>
          </div>
        </div>

        <aside className={styles.right}>
          <div className={styles.inner}>
            <MPU id="mpu_top" />

            {!!teamSites && <FanSiteList list={teamSites} />}

            {latestCommits?.list?.length && (
              <LatestCommits list={latestCommits?.list} />
            )}

            {hasCollegeRankings && (
              <CollegeRankings rankings={collegeRankings} />
            )}

            <MPU id="mpu_middle" />

            <MobileMPU id="mobile_mpu_middle" incontent />
          </div>
        </aside>

        <aside className={styles.left}>
          <div className={styles.inner}>
            {!!trendingList && (
              <div className={styles.trending}>
                <TopHeadlines trendingList={trendingList} />
                <SignUpModule title="Get the Daily On3 Newsletter in your inbox every morning" />
              </div>
            )}

            {hasHighSchoolRankings && (
              <HighSchoolRankings rankings={highSchoolRankings} />
            )}

            {!!list?.length && (
              <Nil100Widget isModule list={list?.slice(0, 5)} />
            )}

            {!!on3Data && <Connect list={on3Data} title="On3 Connect" />}
          </div>
        </aside>
      </HomeLayout>
      <SignUpFooter />
    </div>
  );
};
